import useAuthenticatedQuery from "./useAuthenticatedQuery";
import { useMemo } from "react";

const useGetMetadata = () => {
  // Fetching multiple data sets
  const metadataQuery = useAuthenticatedQuery(
    ["metadata"],
    "/api/v1/organizations/metadata",
    { staleTime: 5 * 60 * 1000 }
  );
  const userGroupsQuery = useAuthenticatedQuery(
    ["userGroups"],
    "/api/v1/organizations/user-groups",
    { staleTime: 5 * 60 * 1000 }
  );

  const allSitesQuery = useAuthenticatedQuery(
    ["allsites"],
    "/api/v1/metadata/sites",
    { staleTime: 5 * 60 * 1000 }
  );

  const workflowsQuery = useAuthenticatedQuery(
    ["workflows"],
    "/api/v1/metadata/workflows",
    { staleTime: 5 * 60 * 1000 }
  );

  const usersQuery = useAuthenticatedQuery(["usersall"], "/api/v1/users/all", {
    staleTime: 5 * 60 * 1000,
  });

  const inputTypeQuery = useAuthenticatedQuery(
    ["inputtypes"],
    "/api/v1/metadata/workflows/input-types",
    { staleTime: 5 * 60 * 1000 }
  );

  const observationPriorities = useAuthenticatedQuery(
    ["observationPriorities"],
    "/api/v1/metadata/observation-priorities",
    { staleTime: 5 * 60 * 1000 }
  );

  const checklistsQuery = useAuthenticatedQuery(
    ["checklists"],
    "/api/v1/metadata/checklists",
    { staleTime: 5 * 60 * 1000 }
  );

  // Destructuring query data and loading/error states
  const {
    data: metadata,
    isLoading: metadataLoading,
    error: errorMetadata,
    refetch: refetchMetadata,
  } = metadataQuery;
  const {
    data: userGroups,
    isLoading: userGroupsLoading,
    error: errorUserGroups,
    refetch: refetchUserGroup,
  } = userGroupsQuery;

  const {
    data: allSites,
    isLoading: sitesLoading,
    error: errorSites,
    refetch: refetchAllSites,
  } = allSitesQuery;

  const {
    data: WorkFlows,
    isLoading: workflowsLoading,
    error: errorWorkflows,
    refetch: refetchWorkflows,
  } = workflowsQuery;

  const {
    data: Users,
    isLoading: usersLoading,
    error: errorUsers,
    refetch: refetchUsers,
  } = usersQuery;
  const {
    data: inputTypes,
    isLoading: typesLoading,
    error: errorTypes,
    refetch: refetchTypes,
  } = inputTypeQuery;

  const {
    data: priorities,
    isLoading: prioritiesLoading,
    error: errorPriorities,
    refetch: refetchPriorities,
  } = observationPriorities;

  const {
    data: checklists,
    isLoading: checklistsLoading,
    error: errorChecklists,
    refetch: refetchChecklists,
  } = checklistsQuery;

  const isLoading =
    metadataLoading ||
    userGroupsLoading ||
    sitesLoading ||
    checklistsLoading ||
    workflowsLoading ||
    usersLoading ||
    prioritiesLoading ||
    typesLoading;

  const error =
    errorMetadata ||
    errorUserGroups ||
    errorSites ||
    errorUsers ||
    errorChecklists ||
    errorWorkflows ||
    errorPriorities ||
    errorTypes;

  const userGroupsList = useMemo(
    () => userGroups?.map(({ id, name }) => ({ value: id, label: name })) || [],
    [userGroups]
  );

  const SitesList = useMemo(
    () => allSites?.map(({ id, name }) => ({ value: id, label: name })) || [],
    [allSites]
  );

  const UsersList = useMemo(
    () =>
      Users?.map(({ id, first_name, last_name }) => ({
        value: id,
        label: `${first_name} ${last_name}`,
      })) || [],
    [Users]
  );

  const Hazards = useMemo(
    () =>
      metadata?.hazards?.map(({ id, name }) => ({ value: id, label: name })) ||
      [],
    [metadata]
  );

  const Precautions = useMemo(
    () =>
      metadata?.precautions?.map(({ id, name }) => ({
        value: id,
        label: name,
      })) || [],
    [metadata]
  );

  const RecoveryElements = useMemo(
    () =>
      metadata?.recovery_elements?.map(({ id, name }) => ({
        value: id,
        label: name,
      })) || [],
    [metadata]
  );

  const WorkFlowList = useMemo(
    () => WorkFlows?.map(({ id, name }) => ({ value: id, label: name })) || [],
    [WorkFlows]
  );

  const Departments = useMemo(
    () =>
      metadata?.departments?.map(({ id, name }) => ({
        value: id,
        label: name,
      })) || [],
    [metadata]
  );

  const RiskRanking = useMemo(
    () =>
      metadata?.risk_rankings?.map(({ id, name }) => ({
        value: id,
        label: name,
      })) || [],
    [metadata]
  );

  const inputTypeList = useMemo(
    () =>
      inputTypes?.map(({ id, display_name }) => ({
        value: id,
        label: display_name,
      })) || [],
    [inputTypes]
  );

  const priorityList = useMemo(
    () => priorities?.map(({ id, name }) => ({ value: id, label: name })) || [],
    [priorities]
  );

  const CheckList = useMemo(
    () => checklists?.map(({ id, name }) => ({ value: id, label: name })) || [],
    [checklists]
  );
  // Remove localStorage effect as we should use API data as source of truth

  return useMemo(
    () => ({
      inputTypeList,
      WorkFlowList,
      UsersList,
      Users,
      Hazards,
      Precautions,
      RecoveryElements,
      Departments,
      RiskRanking,
      userGroupsList,
      SitesList,
      isLoading,
      priorities,
      priorityList,
      checklists,
      CheckList,
      error,
      refetchUsers,
      WorkFlows,
      refetchMetadata,
      refetchUserGroup,
      refetchTypes,
      refetchWorkflows,
      refetchPriorities,
      refetchChecklists,
      refetchAllSites,
      inputTypes,
    }),
    [
      inputTypeList,
      WorkFlowList,
      UsersList,
      Users,
      Hazards,
      Precautions,
      RecoveryElements,
      Departments,
      RiskRanking,
      userGroupsList,
      SitesList,
      isLoading,
      priorities,
      priorityList,
      checklists,
      CheckList,
      error,
      refetchUsers,
      WorkFlows,
      refetchMetadata,
      refetchUserGroup,
      refetchTypes,
      refetchWorkflows,
      refetchPriorities,
      refetchChecklists,
      refetchAllSites,
      inputTypes,
    ]
  );
};

export default useGetMetadata;
